import { createContext, useContext, useMemo } from "react";
import { useImModal } from "@kundeninfo/ui";
import type { Route } from "../../router.js";
import type { ObjektBezug } from "./objektBezug.js";

export type OeffnenOptionen = {
  /** Ruft der Gastgeber beim Schließen, wenn im Panel geschrieben wurde (F9) — die Stelle lädt im Stehen nach. */
  onGeaendert?: () => void;
  /** Seitlich neben dem offenen Dialog (F5); `useObjektPanel` setzt es selbst, wenn die Stelle in einer Hülle steht. */
  seitlich?: boolean;
  /** Das Objekt, das die Stelle selbst als Dialog unter der Ebene zeigt (Ticketdialog der Adresse im Kalender): Nennt die
   *  Ebene es (Panelknopf, Wechsel), schließt sie, statt es ein zweites Mal zu öffnen (F14). */
  darunter?: ObjektBezug;
};

/** Die Wege einer Sprungstelle zum Gastgeber der Objektpanels (Spec F4, F12, F16; PF3). */
export type ObjektPanelWege = {
  /** Öffnet das Objektpanel (ohne Recht oder Inhalt: nichts). */
  oeffne(b: ObjektBezug, o?: OeffnenOptionen): void;
  /** Springt an den Ort des Objekts — durch den Ausgang (`verlasse(sprungZiel(b, route))`). */
  springe(b: ObjektBezug): void;
  /** Der EINE Ausgang (PF3): schließt die Ebenen von oben (Rückfrage bei Ungespeichertem), wartet je Ebene auf das
   *  popstate ihres Verlaufseintrags und navigiert dann genau einmal. Ohne offene Ebene sofort. */
  verlasse(ziel: Route): void;
  kannOeffnen(b: ObjektBezug): boolean;
  kannSpringen(b: ObjektBezug): boolean;
};

/** Ohne Gastgeber (Tests einzelner Bausteine, Zuschnitt ohne Rahmen): kein Knopf, kein Weg. */
const OHNE_GASTGEBER: ObjektPanelWege = {
  oeffne: () => undefined, springe: () => undefined, verlasse: () => undefined,
  kannOeffnen: () => false, kannSpringen: () => false,
};

export const ObjektPanelKontext = createContext<ObjektPanelWege>(OHNE_GASTGEBER);

/**
 * Die Wege des Gastgebers an dieser Stelle (F4). Steht die Stelle in einer Hülle (`useImModal`: in einem Dialog, auch einem
 * Objektpanel), öffnet `oeffne` seitlich (F5, P6) — ohne dass die Stelle es wissen muss.
 */
export function useObjektPanel(): ObjektPanelWege {
  const wege = useContext(ObjektPanelKontext);
  const imModal = useImModal();
  return useMemo(() => (!imModal ? wege
    : { ...wege, oeffne: (b: ObjektBezug, o?: OeffnenOptionen) => wege.oeffne(b, { ...o, seitlich: o?.seitlich ?? true }) }),
  [wege, imModal]);
}

/** Die einzige Entscheidung für Zeilen, die selbst das Ziel sind (F16, P13): Panel, wenn es eines gibt, sonst Sprung. */
export function oeffneOderSpringe(w: ObjektPanelWege, b: ObjektBezug, o?: { onGeaendert?: () => void }): void {
  if (w.kannOeffnen(b)) w.oeffne(b, o);
  else if (w.kannSpringen(b)) w.springe(b);
}
